import { jsPDF } from 'jspdf';

const STATUS_LABELS = {
  EM_ANDAMENTO: 'Em andamento',
  EM_ANALISE: 'Em análise',
  REJEITADO: 'Rejeitado',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
  PENDENTE: 'Pendente',
};

const TIPO_LABEL = {
  CONTRATACAO: 'Contratação',
  ADITIVO: 'Aditivo',
  MINUTA: 'Minuta',
};

function escapeCsv(value) {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

const TERMINAL_STATUSES = ['CONCLUIDO', 'CANCELADO', 'REJEITADO'];

function formatDateShort(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR');
}

function diasNaEtapa(processo) {
  if (TERMINAL_STATUSES.includes(processo?.status)) {
    const fim = formatDateShort(processo.dataFim || processo.updatedAt);
    return fim ? `Finalizado em ${fim}` : 'Finalizado';
  }
  const entrada = processo?.etapaEntradaEm ? new Date(processo.etapaEntradaEm) : null;
  if (!entrada || Number.isNaN(entrada.getTime())) return '';
  return String(Math.floor((Date.now() - entrada.getTime()) / (1000 * 60 * 60 * 24)));
}

function rowData(p) {
  return {
    codigo: p.codigo || p.id || '',
    tipo: TIPO_LABEL[p.tipoFluxo] || p.tipoFluxo || '',
    titulo: p.titulo || '',
    unidade: p.unidade || '',
    prestador: p.prestador?.empresa || p.prestador?.numeroContrato || '',
    etapa: p.etapaAtual?.nome || '',
    responsavel: p.responsavelAtual || '',
    diasEtapa: diasNaEtapa(p),
    status: STATUS_LABELS[p.status] || p.status || '',
  };
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/**
 * Exporta listagem de processos para CSV (Excel).
 */
export function exportProcessosExcel(processos, { label = 'Processos' } = {}) {
  const rows = (processos || []).map(rowData);
  const headers = [
    'Código',
    'Tipo',
    'Título',
    'Unidade',
    'Prestador',
    'Etapa atual',
    'Responsável',
    'Dias na etapa',
    'Status',
  ];
  const lines = [
    headers.join(','),
    ...rows.map((r) =>
      [r.codigo, r.tipo, r.titulo, r.unidade, r.prestador, r.etapa, r.responsavel, r.diasEtapa, r.status]
        .map(escapeCsv)
        .join(',')
    ),
  ];
  const csv = `\uFEFF${lines.join('\r\n')}`;
  const safe = String(label || 'Processos').replace(/[<>:"/\\|?*]/g, '_');
  const stamp = new Date().toISOString().slice(0, 10);
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `processos_${safe}_${stamp}.csv`);
  return rows.length;
}

/**
 * Exporta listagem de processos para PDF.
 */
export function exportProcessosPdf(processos, { label = 'Processos', unidade = '' } = {}) {
  const rows = (processos || []).map(rowData);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const margin = 10;
  let y = 14;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`Processos — ${label}`, margin, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const meta = [
    unidade ? `Unidade: ${unidade}` : null,
    `Gerado em: ${new Date().toLocaleString('pt-BR')}`,
    `Registros: ${rows.length}`,
  ]
    .filter(Boolean)
    .join('  ·  ');
  doc.text(meta, margin, y);
  y += 8;

  const cols = [
    { key: 'codigo', label: 'Código', w: 24 },
    { key: 'tipo', label: 'Tipo', w: 24 },
    { key: 'titulo', label: 'Título', w: 45 },
    { key: 'unidade', label: 'Unidade', w: 18 },
    { key: 'prestador', label: 'Prestador', w: 35 },
    { key: 'etapa', label: 'Etapa', w: 32 },
    { key: 'responsavel', label: 'Responsável', w: 28 },
    { key: 'diasEtapa', label: 'Dias', w: 18 },
    { key: 'status', label: 'Status', w: 24 },
  ];

  const drawHeader = () => {
    doc.setFillColor(227, 242, 253);
    doc.rect(margin, y - 4, 277, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    let x = margin;
    cols.forEach((c) => {
      doc.text(c.label, x + 1, y);
      x += c.w;
    });
    y += 6;
    doc.setFont('helvetica', 'normal');
  };

  drawHeader();

  rows.forEach((r, idx) => {
    if (y > 190) {
      doc.addPage();
      y = 14;
      drawHeader();
    }
    if (idx % 2 === 0) {
      doc.setFillColor(248, 250, 255);
      doc.rect(margin, y - 3.5, 277, 6.5, 'F');
    }
    let x = margin;
    cols.forEach((c) => {
      const text = String(r[c.key] || '—');
      const clipped = doc.splitTextToSize(text, c.w - 2)[0] || '—';
      doc.text(clipped, x + 1, y);
      x += c.w;
    });
    y += 6.5;
  });

  if (!rows.length) {
    doc.setFontSize(10);
    doc.text('Nenhum registro para exportar.', margin, y + 4);
  }

  const safe = String(label || 'Processos').replace(/[<>:"/\\|?*]/g, '_');
  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`processos_${safe}_${stamp}.pdf`);
  return rows.length;
}
